import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Subject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LocateDriverWebsocketService {
  private mainSocket: WebSocket | null = null;
  private individualSocket: WebSocket | null = null;

  private mainConnectionStatus = false;
  private individualConnectionStatus = false;

  private manuallyDisconnectedMain = false;
  private manuallyDisconnectedIndividual = false;

  private currentDriverId: string | number | null = null;

  port = environment.port;

  // Observables for the component to subscribe to
  private mainSocketSubject = new Subject<any>();
  private individualSocketSubject = new Subject<any>();

  constructor() {}

  // ================= MAIN SOCKET (ALL DRIVERS) =================

  public connectMainSocket() {
    if (this.mainConnectionStatus) {
      console.log("Locate Driver Main WS already connected.");
      return;
    }
    this.manuallyDisconnectedMain = false;
    this.openMainConnection();
  }

  private openMainConnection() {
    if (this.mainSocket && this.mainSocket.readyState === WebSocket.OPEN) {
      return;
    }

    let org = localStorage.getItem("org_domain");
    let schema = localStorage.getItem("schema_name");
    let token = localStorage.getItem("access_token");

    if (!org || !schema) {
      console.error("Missing schema_name / org_domain for Locate Driver WS");
      return;
    }

    const host = new URL(org).hostname;

    try {
      this.mainSocket = new WebSocket(
        `wss://${host}:${this.port}/ws/driver-location/${schema}/?token=${token}`
      );
    } catch (error) {
      console.error("Locate Driver Main WS creation failed:", error);
      return;
    }

    this.mainSocket.onopen = () => {
      this.mainConnectionStatus = true;
      console.log("Locate Driver Main WS Connected");
    };

    this.mainSocket.onclose = () => {
      this.mainConnectionStatus = false;
      console.log("Locate Driver Main WS Closed");

      if (!this.manuallyDisconnectedMain) {
        setTimeout(() => this.openMainConnection(), 3000);
      }
    };

    this.mainSocket.onerror = (error) => {
      console.error("Locate Driver Main WS Error:", error);
    };

    this.mainSocket.onmessage = (event: MessageEvent) => {
      try {
        let data = JSON.parse(event.data);
        if (data && data.resultCode === "1" && data.action === "driver_location_update" && data.result?.action === "driver_list") {
          this.mainSocketSubject.next(data.result.results);
        }
      } catch (e) {
        console.error("Locate Driver Main WS JSON parse error:", e);
      }
    };
  }

  public disconnectMainSocket() {
    if (this.mainSocket) {
      this.manuallyDisconnectedMain = true;
      this.mainSocket.close();
      this.mainSocket = null;
      this.mainConnectionStatus = false;
    }
  }

  public onMainUpdate(): Observable<any[]> {
    return this.mainSocketSubject.asObservable();
  }

  // ================= INDIVIDUAL SOCKET (SINGLE DRIVER) =================

  public connectIndividualSocket(driverId: string | number) {
    // If already connected to this driver, do nothing
    if (this.individualConnectionStatus && this.currentDriverId == driverId) {
      return;
    }

    // If connected to a DIFFERENT driver, disconnect first
    this.disconnectIndividualSocket();

    this.currentDriverId = driverId;
    this.manuallyDisconnectedIndividual = false;
    this.openIndividualConnection(driverId);
  }

  private openIndividualConnection(driverId: string | number) {
    if (this.individualSocket && this.individualSocket.readyState === WebSocket.OPEN) {
      return;
    }

    let org = localStorage.getItem("org_domain");
    let schema = localStorage.getItem("schema_name");
    let token = localStorage.getItem("access_token");

    if (!org || !schema) {
      console.error("Missing schema_name / org_domain for Locate Driver Individual WS");
      return;
    }

    const host = new URL(org).hostname;

    try {
      this.individualSocket = new WebSocket(
        `wss://${host}:${this.port}/ws/driver-location/${schema}/${driverId}/?token=${token}`
      );
    } catch (error) {
      console.error("Locate Driver Individual WS creation failed:", error);
      return;
    }

    this.individualSocket.onopen = () => {
      this.individualConnectionStatus = true;
      console.log(`Locate Driver Individual WS Connected (Driver ID: ${driverId})`);
    };

    this.individualSocket.onclose = () => {
      this.individualConnectionStatus = false;
      console.log(`Locate Driver Individual WS Closed (Driver ID: ${driverId})`);

      if (!this.manuallyDisconnectedIndividual && this.currentDriverId == driverId) {
        setTimeout(() => this.openIndividualConnection(driverId), 3000);
      }
    };

    this.individualSocket.onerror = (error) => {
      console.error("Locate Driver Individual WS Error:", error);
    };

    this.individualSocket.onmessage = (event: MessageEvent) => {
      try {
        let data = JSON.parse(event.data);
        if (data && data.resultCode === "1" && data.result?.action === "driver_detail") {
          this.individualSocketSubject.next(data.result.results);
        }
      } catch (e) {
        console.error("Locate Driver Individual WS JSON parse error:", e);
      }
    };
  }

  public disconnectIndividualSocket() {
    if (this.individualSocket) {
      this.manuallyDisconnectedIndividual = true;
      this.individualSocket.close();
      this.individualSocket = null;
      this.individualConnectionStatus = false;
      this.currentDriverId = null;
    }
  }

  public onIndividualUpdate(): Observable<any> {
    return this.individualSocketSubject.asObservable();
  }

  // ================= DISCONNECT ALL =================
  
  public disconnectAll() {
    this.disconnectMainSocket();
    this.disconnectIndividualSocket();
  }
}
